import React, { useState, useCallback } from 'react';
import { connect, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Box, Grid, Typography, TextField } from '@mui/material';
import { StyledTable, MoveOutModal, MoveToMr } from '../../components';
import ModalDialog from '../ModalDialog/ModalDialog';
import JlrButton from '../JlrButton/JlrButton';
import { useResolutionScale } from '../../hooks';
import { useMessageContextManager } from '../../components/MessageOverlay/MessageOverlay';
import { markComplete, markCompleteAndMoveToMr } from '../../redux/actionCreators/printBarcode';
import { moveOutMrStation, clearMrVin } from '../../redux/actionCreators/majorRepair';
import { useRedirectToNextScreen } from '../../hooks';
import {
	AddDefectButton,
	GradientButton,
} from '../../components/TableActionBar/TableActionBar';
import DefectModal from '../ReportModal/DefectModal';
import {
	DARK_MODE,
	MAJOR_REPAIR_MESSAGES,
	MAJOR_REPAIR_MOVE_OUT_REDIRECT_DELAY_MS,
	OPERATIONS,
	REDIRECT_URLS,
	isPuneJlr,
} from '../../constants';
import TakeOut from '../../components/TakeOut/TakeOut';
import MachineDataSection from '../MachineDataSection/MachineDataSection';
import styles from './CommonOperationScreen.module.scss';

const mapStateToProps = (state) => ({
	currentLineItem: state.station.currentLineItem,
	partValidation: state.partValidation,
	receivePart: state.receivePart,
	allowedOperations: state.operations.allowedOperations,
});

const mapDispatchToProps = (dispatch) => ({
	markComplete: (success, error, seq) =>
		dispatch(markComplete(success, error, seq)),
	markCompleteAndMoveToMr: (seq, reason, success, error) =>
		dispatch(markCompleteAndMoveToMr(seq, reason, success, error)),
	moveOutMrStation: (seq, destStation, destLine, success, error) =>
		dispatch(moveOutMrStation(seq, destStation, destLine, success, error)),
	clearMrVin: (success, error) => dispatch(clearMrVin(success, error)),
});

const CommonOperationScreen = ({
	currentLineItem,
	partValidation,
	receivePart,
	markComplete,
	markCompleteAndMoveToMr,
	moveOutMrStation,
	clearMrVin,
	operationName,
	buttonLabel,
	allowedOperations,
}) => {
	const hasMachineOperation = allowedOperations?.includes(OPERATIONS.MACHINE);
	const isMajorRepair = operationName === OPERATIONS.MAJOR_REPAIR || operationName === 'MAJOR_REPAIR';

	// Show "Mark Complete & Move to MR" only for these specific stations
	const MR_COMPLETE_ELIGIBLE_OPS = [
		OPERATIONS.BODY_ALIGN,
		OPERATIONS.WAHA,
		OPERATIONS.RBT,
		OPERATIONS.PAINT_SELECT,
		OPERATIONS.PRE_CAL,
		OPERATIONS.DT,
		OPERATIONS.SHOWER,
	];
	const normalizedOp = operationName?.replace?.(/\s+/g, '_') || operationName;
	const isAfterBookBeforeWtcl = MR_COMPLETE_ELIGIBLE_OPS.includes(normalizedOp) || MR_COMPLETE_ELIGIBLE_OPS.includes(operationName);
	const [isMachineDataComplete, setIsMachineDataComplete] = useState(true);

	const themeMode = useSelector((state) => state?.theme?.themeMode);
	const [isMrCompleteModalOpen, setIsMrCompleteModalOpen] = useState(false);
	const [mrReason, setMrReason] = useState('Defect found — sending to Major Repair for rework');

	const handleMachineStatusChange = useCallback((isComplete) => {
		setIsMachineDataComplete(isComplete);
	}, []);
	const { scaleValue } = useResolutionScale();
	const { showMessageOverlay } = useMessageContextManager();
	const redirect = useRedirectToNextScreen();
	const navigate = useNavigate();

	const serialNumber =
		partValidation.serialNumber ||
		receivePart.serialNumber ||
		currentLineItem?.serialNumber;

	const [isDefectModalOpen, setIsDefectModalOpen] = useState(false);
	const [isMoveOutModalOpen, setIsMoveOutModalOpen] = useState(false);

	const redirectToHomeScreen = () => {
		const isTransferDrop = allowedOperations.includes(OPERATIONS.TRANSFER_DROP);
		const targetUrl = isMajorRepair
			? REDIRECT_URLS.MAJOR_REPAIR_RECEIVE_PART
			: isTransferDrop
			? '/transfer-drop'
			: REDIRECT_URLS[OPERATIONS.RECEIVE_PART];

		redirect(null, {
			operation: isTransferDrop ? OPERATIONS.TRANSFER_DROP : OPERATIONS.RECEIVE_PART,
			redirectUrl: targetUrl,
		});
	};

	const handleActionClick = () => {
		if (isMajorRepair) {
			setIsMoveOutModalOpen(true);
		} else {
			handleMarkComplete();
		}
	};

	const handleConfirmMoveOut = (stationObj) => {
		const destinationStationId = stationObj?.dtStationId || stationObj;
		const destinationLineId = stationObj?.dtLineId;
		if (!currentLineItem?.sequenceNumber) return;
		setIsMoveOutModalOpen(false);

		const closeOverlay = showMessageOverlay({
			contextText: MAJOR_REPAIR_MESSAGES.MOVE_OUT_LOADING,
			loading: true,
		});

		moveOutMrStation(
			currentLineItem?.sequenceNumber,
			destinationStationId,
			destinationLineId,
			() => {
				closeOverlay();
				showMessageOverlay({
					contextText: MAJOR_REPAIR_MESSAGES.MOVE_OUT_SUCCESS,
					success: true,
				});
				setTimeout(() => {
					redirectToHomeScreen();
				}, MAJOR_REPAIR_MOVE_OUT_REDIRECT_DELAY_MS);
			},
			(err) => {
				closeOverlay();
				showMessageOverlay({
					contextText: MAJOR_REPAIR_MESSAGES.MOVE_OUT_ERROR,
					info: err?.response?.data?.message || 'An error occurred during Move Out',
					error: true,
				});
			},
		);
	};

	const handleClearMrVin = () => {
		const closeOverlay = showMessageOverlay({ contextText: MAJOR_REPAIR_MESSAGES.CLEAR_LOADING, loading: true });
		clearMrVin(
			() => {
				closeOverlay();
				showMessageOverlay({ contextText: MAJOR_REPAIR_MESSAGES.CLEAR_SUCCESS, success: true });
				navigate(REDIRECT_URLS[OPERATIONS.MAJOR_REPAIR], { replace: true });
			},
			(err) => {
				closeOverlay();
				showMessageOverlay({ contextText: MAJOR_REPAIR_MESSAGES.CLEAR_ERROR, info: err?.response?.data?.message || MAJOR_REPAIR_MESSAGES.TRY_AGAIN, error: true });
			},
		);
	};

	const handleMarkComplete = () => {
		if (!currentLineItem?.sequenceNumber) return;

		const closeOverlay = showMessageOverlay({
			contextText: `Processing ${operationName}...`,
			loading: true,
		});

		markComplete(
			() => {
				closeOverlay();
				showMessageOverlay({
					contextText: `${operationName} Completed Successfully`,
					success: true,
				});
				setTimeout(() => {
					redirectToHomeScreen();
				}, 2000);
			},
			(err) => {
				closeOverlay();
				showMessageOverlay({
					contextText: `${operationName} Failed`,
					info: err?.response?.data?.message || 'An error occurred',
					error: true,
				});
			},
			currentLineItem?.sequenceNumber,
		);
	};

	const handleMarkCompleteAndMoveToMr = () => {
		if (!currentLineItem?.sequenceNumber) return;
		setIsMrCompleteModalOpen(false);

		const closeOverlay = showMessageOverlay({
			contextText: `Completing ${operationName} and sending to Major Repair...`,
			loading: true,
		});

		markCompleteAndMoveToMr(
			currentLineItem?.sequenceNumber,
			mrReason,
			() => {
				closeOverlay();
				showMessageOverlay({
					contextText: `Completed & Moved to Major Repair`,
					success: true,
				});
				setTimeout(() => {
					redirectToHomeScreen();
				}, 2000);
			},
			(err) => {
				closeOverlay();
				showMessageOverlay({
					contextText: `Move to Major Repair Failed`,
					info: err?.response?.data?.message || 'An error occurred',
					error: true,
				});
			},
		);
	};

	const defaultPartDetailsColumns = [
		{
			title: 'Scanned Serial No./VIN',
			field: 'serialNumber',
			width: 300,
			render: (row) => (
				<Typography sx={{ fontWeight: 'bold', textAlign: 'center' }}>
					{row.serialNumber}
				</Typography>
			),
		},
		{
			title: 'KSPEC',
			field: 'partNumber',
			width: 250,
			render: (row) => (
				<Typography sx={{ fontWeight: 'bold', textAlign: 'center' }}>
					{row.partNumber}
				</Typography>
			),
		},
		{
			title: isPuneJlr() ? 'Model' : 'KSPEC Description',
			field: isPuneJlr() ? 'model' : 'description',
			width: 450,
			render: (row) => (
				<Typography sx={{ fontWeight: 'bold', textAlign: 'center' }}>
					{isPuneJlr() ? (currentLineItem?.model || row.description) : row.description}
				</Typography>
			),
		},
		{
			title: 'Lot Number',
			field: 'lotNumber',
			width: 250,
			render: (row) => (
				<Typography sx={{ fontWeight: 'bold', textAlign: 'center' }}>
					{currentLineItem?.lotNumber || row.lotNumber}
				</Typography>
			) 
		},
	];

	const defaultPartDetailsData = [
		{
			serialNumber: serialNumber || '-',
			partNumber:
				currentLineItem?.partNumber || currentLineItem?.vcPartNumber || '-',
			description:
				currentLineItem?.description || currentLineItem?.vcDescription || '-',
			model: currentLineItem?.model || currentLineItem?.description || currentLineItem?.vcDescription || '-', 
			lotNumber: currentLineItem?.lotNumber || '-',
		},
	];

	const mrColumns = [
		{
			title: 'ASN',
			field: 'asn',
			width: 180,
			render: (row) => (
				<Typography sx={{ fontWeight: 'bold', textAlign: 'center' }}>
					{row.asn}
				</Typography>
			),
		},
		{
			title: 'SERIAL NUMBER (VIN)',
			field: 'serialNumber',
			width: 260,
			render: (row) => (
				<Typography sx={{ fontWeight: 'bold', textAlign: 'center' }}>
					{row.serialNumber}
				</Typography>
			),
		},
		{
			title: 'MODEL',
			field: 'model',
			width: 200,
			render: (row) => (
				<Typography sx={{ fontWeight: 'bold', textAlign: 'center' }}>
					{row.model}
				</Typography>
			),
		},
		{
			title: 'KSPEC',
			field: 'kspec',
			width: 220,
			render: (row) => (
				<Typography sx={{ fontWeight: 'bold', textAlign: 'center' }}>
					{row.kspec}
				</Typography>
			),
		},
		{
			title: 'LOT NO',
			field: 'lotNo',
			width: 180,
			render: (row) => (
				<Typography sx={{ fontWeight: 'bold', textAlign: 'center' }}>
					{row.lotNo}
				</Typography>
			),
		},
		{
			title: 'ORIGIN STATION',
			field: 'originStation',
			width: 240,
			render: (row) => (
				<Typography sx={{ fontWeight: 'bold', textAlign: 'center' }}>
					{row.originStation}
				</Typography>
			),
		},
	];

	const mrData = [
		{
			asn: currentLineItem?.asn || currentLineItem?.sequenceNumber || '-',
			serialNumber: serialNumber || '-',
			model: currentLineItem?.model || '-',
			kspec: currentLineItem?.kspec || '-',
			lotNo: currentLineItem?.lotNo || '-',
			originStation: currentLineItem?.lineTakeOutStationDescription || 
				(currentLineItem?.lineTakeOutStationId ? `Station ${currentLineItem.lineTakeOutStationId}` : '-'),
		},
	];

	const partDetailsColumns = isMajorRepair ? mrColumns : defaultPartDetailsColumns;
	const partDetailsData = isMajorRepair ? mrData : defaultPartDetailsData;

	return (
		<Box className={styles.pageContainer}>
			<Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
				<StyledTable
					title="Current KSPEC"
					columns={partDetailsColumns}
					rows={partDetailsData}
					forceHeight={scaleValue(120)}
					showFilter={false}
					showColumns={false}
					elevated
				/>

				{hasMachineOperation && (
					<MachineDataSection
						resolvedSerialNumber={serialNumber}
						sequenceNumber={currentLineItem?.sequenceNumber}
						onMachineStatusChange={handleMachineStatusChange}
					/>
				)}
			</Box>

			<Box className={styles.footer}>
				<Grid container justifyContent="space-between" alignItems="center">
					<Grid item sx={{ display: 'flex', gap: 2 }}>
						{!isMajorRepair && (
							<AddDefectButton
								onClick={() => setIsDefectModalOpen(true)}
								disabled={!serialNumber}
							/>
						)}
						{!isMajorRepair && (
							<TakeOut
								partDetails={{
									serialNumber,
									sequenceNumber: currentLineItem?.sequenceNumber,
								}}
								redirectToNextScreen={redirectToHomeScreen}
								btnStyles={{
									height: 56,
									px: 4,
									borderRadius: '12px',
									background: 'linear-gradient(90deg, #CB1D49 0%, #C71616 100%)',
									fontSize: scaleValue(18),
									'& svg': { display: 'none' },
									'&:hover': {
										background: 'linear-gradient(90deg, #CB1D49 0%, #C71616 100%) !important',
									},
								}}
							/>
						)}
						{!isMajorRepair && operationName !== OPERATIONS.ENGRAVING && (
							<MoveToMr
								partDetails={{
									serialNumber,
									sequenceNumber: currentLineItem?.sequenceNumber,
								}}
							/>
						)}
					</Grid>
					<Grid item sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
						{isAfterBookBeforeWtcl && (
							<GradientButton
								onClick={() => setIsMrCompleteModalOpen(true)}
								gradient="linear-gradient(90deg, #FF6B35 0%, #F7931E 100%)"
								sx={{ height: 56, px: 4, borderRadius: '12px', whiteSpace: 'nowrap' }}
								disabled={!serialNumber}
							>
								Mark Complete &amp; Move to MR
							</GradientButton>
						)}
						<GradientButton
							onClick={handleActionClick}
							gradient="linear-gradient(90deg, #2196F3 0%, #21CCE1 100%)"
							sx={{ height: 56, px: 8, borderRadius: '12px' }}
							disabled={!serialNumber || (hasMachineOperation && !isMachineDataComplete)}
						>
							{isMajorRepair ? 'MOVE OUT' : (buttonLabel || `Mark ${operationName}`)}
						</GradientButton>
		{isMajorRepair && (
							<GradientButton
								gradient="linear-gradient(90deg, #E53935 0%, #B71C1C 100%)"
								onClick={handleClearMrVin}
								sx={{ height: 56, px: 8, borderRadius: '12px' }}
							>
								CLEAR VIN
							</GradientButton>
						)}
					</Grid>
				</Grid>
			</Box>

			<DefectModal
				open={isDefectModalOpen}
				handleClose={() => setIsDefectModalOpen(false)}
				partDetailsRow={partDetailsData.map((row) => ({
					serialNumber: row.serialNumber,
					parentPartNumber: row.partNumber,
					parentPartDescription: row.description,
				}))}
				partDetails={{
					serialNumber,
					sequenceNumber: currentLineItem?.sequenceNumber,
				}}
			/>

			<MoveOutModal
				open={isMoveOutModalOpen}
				onClose={() => setIsMoveOutModalOpen(false)}
				onConfirm={handleConfirmMoveOut}
				sequenceNumber={currentLineItem?.sequenceNumber}
			/>

			<ModalDialog
				open={isMrCompleteModalOpen}
				title="Mark Complete & Move to Major Repair"
				onClose={() => setIsMrCompleteModalOpen(false)}
				modalWidth={scaleValue(600)}
				modalHeight="auto"
			>
				<Box sx={{ p: 4, textAlign: 'center' }}>
					<Typography
						variant="h6"
						sx={{
							mb: 2,
							fontWeight: 600,
							color: themeMode === DARK_MODE ? '#fff' : '#222',
							fontSize: scaleValue(22),
						}}
					>
						Are you sure you want to mark {operationName} complete and move vehicle (VIN/ASN: {serialNumber || currentLineItem?.sequenceNumber}) to Major Repair?
					</Typography>

					<TextField
						fullWidth
						multiline
						rows={3}
						label="Reason for Major Repair"
						value={mrReason}
						onChange={(e) => setMrReason(e.target.value)}
						sx={{
							my: 2,
							'& .MuiInputBase-root': {
								color: themeMode === DARK_MODE ? '#fff' : '#000',
							},
						}}
					/>

					<Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3 }}>
						<JlrButton
							variant="outlined"
							size="medium"
							onClick={() => setIsMrCompleteModalOpen(false)}
							sx={{ minWidth: scaleValue(140), width: 'auto' }}
						>
							Cancel
						</JlrButton>
						<JlrButton
							variant="primary"
							size="medium"
							onClick={handleMarkCompleteAndMoveToMr}
							sx={{
								minWidth: scaleValue(200),
								width: 'auto',
								background: 'linear-gradient(90deg, #FF6B35 0%, #F7931E 100%)',
							}}
						>
							Mark Complete &amp; Move to MR
						</JlrButton>
					</Box>
				</Box>
			</ModalDialog>
		</Box>
	);
};

export default connect(
	mapStateToProps,
	mapDispatchToProps,
)(CommonOperationScreen);
